import {createContext, useContext} from 'react';

/**
 * Context for accessing the Shadow DOM root.
 * Components inside the shadow DOM should use this instead of `document` queries.
 */
const ShadowRootContext = createContext(null);

/**
 * Hook to get the shadow root for DOM queries.
 * Returns the shadow root container element, or document as fallback.
 * 
 * Usage:
 * ```jsx
 * const shadowRoot = useShadowRoot();
 * const element = shadowRoot.querySelector('.my-class');
 * const elements = shadowRoot.querySelectorAll('.my-class');
 * ```
 */
export const useShadowRoot = () => {
  const shadowRoot = useContext(ShadowRootContext);
  // Return document as fallback for standalone usage (not in shadow DOM)
  return shadowRoot || document;
};

/**
 * Hook to get an element by class name within the shadow DOM.
 * Replacement for document.getElementsByClassName() that works in shadow DOM.
 * 
 * @param {string} className - The class name to search for
 * @returns {Element|null} The first element with the class, or null
 */
export const useQuerySelector = () => {
  const root = useShadowRoot();
  return (selector) => root.querySelector(selector);
};

/**
 * Hook to get elements by class name within the shadow DOM.
 * Replacement for document.getElementsByClassName() that works in shadow DOM.
 * 
 * @returns {Function} A function that takes a class name and returns elements
 */
export const useGetElementsByClassName = () => {
  const root = useShadowRoot();
  return (className) => root.getElementsByClassName?.(className) || root.querySelectorAll(`.${className}`);
};

export default ShadowRootContext;
